import { useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useAuth } from '@/auth/useAuth'
import PasswordInput from '@/components/PasswordInput'
import type { ApiError } from '@/types'

export default function LoginPage() {
  const { login } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const statusMessage = (location.state as { message?: string } | null)?.message
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setError(null)
    setSubmitting(true)
    try {
      await login(username, password)
      navigate('/nodes', { replace: true })
    } catch (err) {
      const apiErr = err as ApiError
      if (apiErr.status === 401) {
        setError('Invalid username or password.')
      } else {
        setError(apiErr.message ?? 'Login failed.')
      }
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div style={styles.page}>
      <div style={styles.card}>
        <h1 style={styles.title}>Node Manager</h1>
        <p style={styles.subtitle}>Sign in to continue</p>
        {statusMessage && <p style={styles.success}>{statusMessage}</p>}
        <form onSubmit={handleSubmit} style={styles.form}>
          <label style={styles.label}>
            Username
            <input
              style={styles.input}
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              autoComplete="username"
              required
            />
          </label>
          <div style={styles.label}>
            <label htmlFor="login-password">Password</label>
            <PasswordInput
              id="login-password"
              accessibleName="password"
              value={password}
              onChange={setPassword}
              autoComplete="current-password"
              inputStyle={styles.input}
            />
          </div>
          {error && <p style={styles.error}>{error}</p>}
          <button style={styles.button} type="submit" disabled={submitting}>
            {submitting ? 'Signing in...' : 'Sign in'}
          </button>
        </form>
      </div>
    </div>
  )
}

const styles: Record<string, React.CSSProperties> = {
  page: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: '#f5f5f7',
    fontFamily: 'system-ui, sans-serif',
  },
  card: {
    background: '#fff',
    borderRadius: 8,
    padding: '40px 36px',
    boxShadow: '0 2px 12px rgba(0,0,0,0.1)',
    width: 360,
  },
  title: {
    margin: '0 0 4px',
    fontSize: 22,
    fontWeight: 700,
    color: '#1a1a2e',
  },
  subtitle: {
    margin: '0 0 28px',
    fontSize: 14,
    color: '#666',
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
  },
  label: {
    display: 'flex',
    flexDirection: 'column',
    gap: 6,
    fontSize: 13,
    fontWeight: 600,
    color: '#333',
  },
  input: {
    padding: '8px 12px',
    border: '1px solid #d0d0d8',
    borderRadius: 4,
    fontSize: 14,
    outline: 'none',
    fontFamily: 'inherit',
  },
  error: {
    margin: 0,
    fontSize: 13,
    color: '#d32f2f',
    background: '#fdecea',
    padding: '8px 12px',
    borderRadius: 4,
  },
  success: {
    margin: '0 0 16px',
    fontSize: 13,
    color: '#2e7d32',
    background: '#e8f5e9',
    padding: '8px 12px',
    borderRadius: 4,
  },
  button: {
    padding: '10px 0',
    background: '#1a1a2e',
    color: '#fff',
    border: 'none',
    borderRadius: 4,
    fontSize: 14,
    fontWeight: 600,
    cursor: 'pointer',
    marginTop: 4,
  },
}
